/**
 * Unidades (§3, [NO NEGOCIABLE]).
 *
 * Regla que gobierna este archivo: **se almacena el valor tal como el usuario lo leyó,
 * junto con su unidad**. Acá no hay ninguna función que "normalice para guardar". Las
 * conversiones existen para dos cosas y sólo dos:
 *   1. mostrar ambas unidades en la UI de lectura;
 *   2. poder sumar peras con manzanas al agregar KPIs (una máquina en libras y otra en
 *      kilos en el mismo tonelaje semanal), lo cual ocurre en memoria y nunca se persiste.
 */

import type { Maquina, Unidad } from './types.ts';

export const LB_POR_KG = 2.20462262185;

export interface Peso {
  valor: number;
  unidad: Unidad;
}

export function kgALb(kg: number): number {
  return kg * LB_POR_KG;
}

export function lbAKg(lb: number): number {
  return lb / LB_POR_KG;
}

export function convertir(valor: number, de: Unidad, a: Unidad): number {
  if (de === a) return valor;
  return de === 'kg' ? kgALb(valor) : lbAKg(valor);
}

/** Valor en kg, sin redondear. Para agregaciones en memoria. */
export function enKg(p: Peso): number {
  return p.unidad === 'kg' ? p.valor : lbAKg(p.valor);
}

/** Valor en lb, sin redondear. Para agregaciones en memoria. */
export function enLb(p: Peso): number {
  return p.unidad === 'lb' ? p.valor : kgALb(p.valor);
}

export function enUnidad(p: Peso, a: Unidad): number {
  return convertir(p.valor, p.unidad, a);
}

// ---------------------------------------------------------------------------
// Presentación
// ---------------------------------------------------------------------------

/**
 * Número con coma decimal y sin ceros de relleno: 25 → "25", 11.34 → "11,3".
 * `decimales` es el máximo, no el mínimo.
 */
export function formatearNumero(valor: number, decimales = 1): string {
  if (!Number.isFinite(valor)) return '—';
  const factor = 10 ** decimales;
  const r = Math.round(valor * factor) / factor;
  return (Number.isInteger(r) ? String(r) : r.toFixed(decimales)).replace('.', ',');
}

export function formatearPeso(p: Peso, decimales = 1): string {
  return `${formatearNumero(p.valor, decimales)} ${p.unidad}`;
}

/**
 * Ambas unidades a la vez: "25 lb · 11,3 kg" (§3, "mostrar siempre ambas unidades en la
 * UI de lectura"). La unidad original va primero: es el número que el usuario vio en la
 * placa, y el que tiene que reconocer cuando vuelva a la máquina.
 */
export function formatearDual(p: Peso, separador = ' · '): string {
  const otra: Unidad = p.unidad === 'kg' ? 'lb' : 'kg';
  return `${formatearPeso(p)}${separador}${formatearPeso({ valor: enUnidad(p, otra), unidad: otra })}`;
}

/** "30:34" o "1:02:15". */
export function formatearDuracion(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '—';
  const s = Math.round(segundos);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const seg = s % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  return h > 0
    ? `${h}:${mm}:${String(seg).padStart(2, '0')}`
    : `${mm}:${String(seg).padStart(2, '0')}`;
}

/** "30:34" / "30m 34s" / "1834" → segundos. Para la edición manual del OCR. */
export function parsearDuracion(texto: string): number | null {
  const t = texto.trim().toLowerCase();
  if (!t) return null;

  const conDosPuntos = t.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (conDosPuntos) {
    const [, a, b, c] = conDosPuntos;
    return c === undefined
      ? Number(a) * 60 + Number(b)
      : Number(a) * 3600 + Number(b) * 60 + Number(c);
  }

  const conLetras = t.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/);
  if (conLetras && conLetras.slice(1).some(Boolean)) {
    const [, h, m, s] = conLetras;
    return Number(h ?? 0) * 3600 + Number(m ?? 0) * 60 + Number(s ?? 0);
  }

  const soloNumero = t.match(/^\d+$/);
  return soloNumero ? Number(t) : null;
}

// ---------------------------------------------------------------------------
// Incrementos de placa (§3 y §6.1)
// ---------------------------------------------------------------------------

export const INCREMENTO_POR_DEFECTO: Record<Unidad, number> = { lb: 5, kg: 2.5 };

export function incrementoDe(maquina: Maquina | null | undefined, unidad: Unidad): number {
  if (maquina && maquina.incremento_placa > 0) return maquina.incremento_placa;
  return INCREMENTO_POR_DEFECTO[unidad];
}

/** Redondea al múltiplo de `incremento` más cercano. */
export function alIncremento(valor: number, incremento: number): number {
  if (!(incremento > 0)) return valor;
  return Math.round(valor / incremento) * incremento;
}

/**
 * El siguiente peso que la máquina realmente admite. Sube al menos un incremento, incluso
 * si el peso actual está entre dos placas: en una máquina que va de 5 en 5, sugerir
 * "+2,5 lb" es sugerir algo que no se puede cargar.
 */
export function siguienteSalto(pesoActual: number, incremento: number, saltos = 1): number {
  if (!(incremento > 0)) return pesoActual;
  const base = Math.floor(pesoActual / incremento) * incremento;
  const siguiente = base + incremento * saltos;
  return Number(siguiente.toFixed(4));
}

export function saltoAnterior(pesoActual: number, incremento: number): number {
  if (!(incremento > 0)) return pesoActual;
  const base = Math.ceil(pesoActual / incremento) * incremento;
  return Number(Math.max(0, base - incremento).toFixed(4));
}
